<template>
  <v-container>
    <!-- Header -->
    <v-row class="justify-center">
      <v-col class="col-auto display-2 font-weight-bold">Congratulations!</v-col>
    </v-row>

    <!-- Number Order Status -->
    <div class="headline mb-3 blue--text">Your Number</div>
    <div class="my-3">
      It may take up to 5 minutes for a number to be assigned. You can check the current status of your order below. When returning to HawkSoft Text Messaging in the Marketplace, the number or status will be shown.
    </div>
    <div v-if="fOrderStatus === -1">
      <div>Your previous phone number order failed for that area of zip code. Please re-enter a different Area Code or Zip Code and submit a new order.</div>
      <div>If you are still unable to get a new number, please contact us at insidesales@hawksoft.com or (866)884-4680 option 4.</div>
    </div>

    <v-row no-gutters>
      <TSOrderStatus
        v-if="!isLoading"
        :order-status="fOrderStatus"
        :selected-num="selectedNum"
        @update="loadTextingProfile"
      />
      <TSPhoneLookup
        v-if="fOrderStatus === -1"
        :area-code.sync="areaCode"
        :zip-code.sync="zipCode"
        @error="setValidationErr"
      />
      <div v-if="fOrderStatus === -1" justify-end>
        <v-btn color="callToAction" :disabled="!confValid || isLoading" :loading="isLoading" @click="orderPhoneNumber">
          <span>Finish</span>
        </v-btn>
      </div>
    </v-row>

    <!-- Next Steps -->
    <div v-if="payFeatureSwitchEnabled">
      <div class="headline my-3 blue--text">Next Steps</div>
      <div class="my-3">Here are the next steps that must be completed before HawkSoft Text Messaging will be activated for your agency:</div>
      <ol>
        <li>Navigate to <a target="_blank" href="https://www.hawksoft.com/10dlc">The Campaign Registry (TCR) Required Information Form</a> and fill out the required information.</li>
        <ol>
          a. If this form is not filled out, a HawkSoft representative will contact you.
        </ol>
        <li>HawkSoft will submit the registration data to (TCR). Once registered, HawkSoft will activate HawkSoft Text Messaging for the agency.</li>
        <li>HawkSoft will notify the agency main contacts that HawkSoft Text Messaging is active. Anyone who closes and relaunches HawkSoft CMS will see HawkSoft Text Messaging at this point.</li>
      </ol>
    </div>

    <!-- Texting Admins -->
    <div class="headline my-3 blue--text">
      {{ payFeatureSwitchEnabled ? "Texting Admins" : "Administration" }}
    </div>
    <div class="px-4">
      <span v-if="payFeatureSwitchEnabled === false">
        You can now assign your users as "Texting Admins" in User List permissions. A texting admin is a user who is able to see all incoming text messages received by the agency regardless of client permissions.
        In the future, we will add more tasks a texting admin will be able to perform.
      </span>
      <span v-if="payFeatureSwitchEnabled === true">
        You can now assign users as "Texting Admins" in User List permissions. A Texting Admin is a user who is able to see all incoming text messages received by the agency regardless of client permissions.
      </span>
    </div>

    <div class="headline my-3 blue--text">Helpful Tip</div>
    <div class="px-4">
      <ul>
        <li>Phone numbers stored for your customers in HawkSoft CMS must be labeled as Cell Phone for HawkSoft Text Messaging to be able to send texts to those numbers.</li>
        <li v-if="payFeatureSwitchEnabled">To receive notifications for texts on a client file, you must be set as the client CSR or Client Producer, or follow that client.</li>
      </ul>
    </div>

    <v-row v-if="!isLoading" class="justify-end">
      <v-col class="col-auto">
        <v-btn color="success" :disabled="!fOrderStatus === 1" @click="returnToMarketplace()">Return To Marketplace</v-btn>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="hasError"
      :timeout="3000"
      color="error"
    >
      {{ errorMessage }}
    </v-snackbar>

  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import TSOrderStatus from './TSOrderStatus.vue';
import TSPhoneLookup from './TSPhoneLookup.vue';

export default {
  name: 'TextingSetupFinish',
  components: {
    TSOrderStatus,
    TSPhoneLookup
  },
  data: () => ({
    hasError: false,
    errorMessage: '',
    isLoading: false,
    optInMode: 0,
    orderStatus: 0,
    selectedNum: '',
    areaCode: '',
    zipCode: '',
    hasValidationErr: false
  }),
  computed: {
    ...mapState([
      'agency',
      'featureSwitches'
    ]),
    confValid () {
      const oneIsPopulated = (this.areaCode.length > 0 || this.zipCode.length > 0);
      const bothArePopulated = (this.areaCode.length > 0 && this.zipCode.length > 0);
      return oneIsPopulated && !bothArePopulated;
    },
    fOrderStatus () {
      if (this.orderStatus === 0 && !this.numberExists) return 0; // pending
      if (this.numberExists || this.orderStatus === 1) return 1; // completed
      if (this.orderStatus === -1 && !this.numberExists) return -1; // failed
      return 0;
    },
    numberExists () {
      return ((this.selectedNum || null) !== null);
    },
    payFeatureSwitchEnabled () {
      if (this.featureSwitches === null) {
        return null;
      }
      if (this.featureSwitches.TextingProductStatusToggle == null) {
        return false;
      }
      return this.featureSwitches.TextingProductStatusToggle;
    }
  },
  async created () {
    this.isLoading = true;
    const response = await this.$api.getOrderInfo();
    if (response && response.data && response.data.code === 0) {
      this.orderStatus = response.data.data[0].status;
    } else {
      this.setError('Something went wrong when retrieving your order status.');
    }
    let p1 = this.loadTextingProfile();
    let p2 = this.getFeatureSwitches();
    await Promise.all([p1, p2]);
    this.isLoading = false;
  },
  methods: {
    ...mapActions([
      'getFeatureSwitches'
    ]),
    async loadTextingProfile () {
      const response = await this.$api.getTextingProfile(this.agency.agencyId);
      if (response.data) {
        const agencyData = response.data.data[0];
        if ((agencyData || null) !== null) {
          this.optInMode = agencyData.optInMode;
          this.selectedNum = agencyData.phones.length > 0 ? agencyData.phones[0].phone : '';
        }
      }
    },
    setValidationErr (bool) {
      this.hasValidationErr = bool;
    },
    setError (m) {
      this.hasError = true;
      this.errorMessage = m;
    },
    returnToMarketplace () {
      this.$router.push({ name: 'Marketplace' });
    }
  }
};
</script>
